import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, User, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", address: "", notes: "" };

export default function Employees() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => { const { data } = await http.get("/employees"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await http.put(`/employees/${editingId}`, form);
      else await http.post("/employees", form);
      toast.success("Kaydedildi");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await http.delete(`/employees/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{items.length} çalışan</div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-employee-button">
              <Plus className="h-4 w-4 mr-2" /> Yeni Çalışan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Çalışan Düzenle" : "Yeni Çalışan"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Ad Soyad *</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="employee-name-input" /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="employee-phone-input" /></div>
              <div><Label>Adres</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
              <div><Label>Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
              <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-employee-button">Kaydet</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-grid">
        {items.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">Henüz çalışan yok</div>}
        {items.map((em) => (
          <Card key={em.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-lg">
                {em.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">{em.name}</div>
                <div className="space-y-1.5 mt-2 text-xs text-slate-600">
                  {em.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {em.phone}</div>}
                  {em.address && <div className="flex items-start gap-1.5"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /> <span className="line-clamp-2">{em.address}</span></div>}
                </div>
              </div>
            </div>
            {em.notes && <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3">{em.notes}</div>}
            <div className="mt-3 flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setForm(em); setEditingId(em.id); setOpen(true); }} data-testid={`edit-employee-${em.id}`}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(em.id)} data-testid={`delete-employee-${em.id}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
